import { useCallback } from 'react';

import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useUpdateMemoMutation } from '@/modules/memos/graphql/UpdateMemo.generated';

interface SaveMemoInput {
  memoId: string;
  memoName: string;
  memoContent: string;
}

export function useSaveMemo() {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [runSaveMemo, { loading }] = useUpdateMemoMutation();

  const saveMemo = useCallback(
    async ({ memoId, memoName, memoContent }: SaveMemoInput) => {
      try {
        await runSaveMemo({
          variables: {
            input: {
              id: memoId,
              update: {
                name: memoName,
                contentEncrypted: memoContent,
              },
            },
          },
          refetchQueries: 'active',
          awaitRefetchQueries: true,
        });

        showFeedback(FeedbackMessages.formSaveSuccess, {
          variant: 'success',
        });
        return true;
      } catch (error) {
        showFeedback(FeedbackMessages.formSaveError);
        reportError(
          'Failed to save memo',
          error instanceof Error ? error : new Error('Unknown save error'),
          { memoId }
        );
        return false;
      }
    },
    [reportError, runSaveMemo, showFeedback]
  );

  return {
    saveMemo,
    loading,
  };
}
